import React, { useState, useRef, useCallback } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  NumberInput,
  Alert,
  Checkbox,
  Tooltip,
  CloseButton,
  Box,
  SegmentedControl,
} from "@mantine/core";
import { notifySuccess } from "../../utils/notify";
import {
  IconPercentage,
  IconPhoto,
  IconUpload,
  IconAlertCircle,
  IconCheck,
  IconInfoCircle,
} from "@tabler/icons-react";
import styles from "./TerminalConfigurationModal.module.css";

interface TippingConfig {
  percentages: number[];
  fixed_amounts: number[];
  smart_tip_threshold: number | null;
}

interface TerminalConfigurationModalProps {
  opened: boolean;
  onClose: () => void;
  accountId: string;
  setTerminalConfiguration: (configData: any) => Promise<any>;
}

const MAX_IMAGE_SIZE_MB = 2;
const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg"];
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

const TerminalConfigurationModal: React.FC<TerminalConfigurationModalProps> = ({
  opened,
  onClose,
  accountId,
  setTerminalConfiguration,
}) => {
  // Tipping state
  const [tipping, setTipping] = useState<TippingConfig>({
    percentages: [15, 20, 25],
    fixed_amounts: [100, 200, 300],
    smart_tip_threshold: 1000,
  });
  const [useSmartTip, setUseSmartTip] = useState(true);
  const [tipType, setTipType] = useState<"percentages" | "fixed_amounts">(
    "percentages",
  );

  // Splashscreen state
  const [splashscreenData, setSplashscreenData] = useState<string | null>(null);
  const [splashscreenFilename, setSplashscreenFilename] = useState<string>("");
  const [splashscreenPreview, setSplashscreenPreview] = useState<string | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tipping handlers
  const updatePercentage = (index: number, value: number | string) => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return;
    const updated = [...tipping.percentages];
    updated[index] = numValue;
    setTipping({ ...tipping, percentages: updated });
  };

  const updateFixedAmount = (index: number, value: number | string) => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return;
    const updated = [...tipping.fixed_amounts];
    updated[index] = Math.round(numValue * 100); // Convert to cents
    setTipping({ ...tipping, fixed_amounts: updated });
  };

  // Splashscreen handlers
  const handleFileSelect = useCallback((file: File) => {
    setError(null);

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError("Splash screen must be a PNG or JPG image.");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setError(`Splash screen image must be under ${MAX_IMAGE_SIZE_MB}MB.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      // Create preview URL
      setSplashscreenPreview(result);
      // Extract base64 data (remove data:image/png;base64, prefix)
      const base64Data = result.split(",")[1];
      setSplashscreenData(base64Data);
      setSplashscreenFilename(file.name);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const removeSplashscreen = () => {
    setSplashscreenData(null);
    setSplashscreenFilename("");
    setSplashscreenPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Determine which tip fields are active based on smart tip / tipType
  const showPercentages = useSmartTip || tipType === "percentages";
  const showFixedAmounts = useSmartTip || tipType === "fixed_amounts";

  // Validation
  const validate = (): string | null => {
    if (showPercentages) {
      for (const pct of tipping.percentages) {
        if (pct <= 0 || pct > 100) {
          return "Tip percentages must be between 1 and 100.";
        }
      }
    }

    if (showFixedAmounts) {
      for (const amt of tipping.fixed_amounts) {
        if (amt <= 0) {
          return "Fixed tip amounts must be greater than $0.";
        }
      }
    }

    if (
      useSmartTip &&
      (tipping.smart_tip_threshold === null || tipping.smart_tip_threshold <= 0)
    ) {
      return "Smart tip threshold must be greater than $0.";
    }

    return null;
  };

  // Submit handler
  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const configData: any = { accountId };

      const tippingPayload: any = {};
      if (showPercentages && tipping.percentages.length > 0) {
        tippingPayload.percentages = tipping.percentages;
      }
      if (showFixedAmounts && tipping.fixed_amounts.length > 0) {
        tippingPayload.fixed_amounts = tipping.fixed_amounts;
      }
      if (useSmartTip && tipping.smart_tip_threshold !== null) {
        tippingPayload.smart_tip_threshold = tipping.smart_tip_threshold;
      }
      configData.tipping = tippingPayload;

      if (splashscreenData) {
        configData.splashscreen = {
          data: splashscreenData,
          filename: splashscreenFilename || "splashscreen.png",
        };
      }

      await setTerminalConfiguration(configData);
      onClose();
      notifySuccess("Changes may take a moment to appear on the device.", {
        title: "Terminal configured",
        icon: <IconCheck size="1rem" />,
      });
    } catch (err: any) {
      console.error("Error configuring terminal:", err);
      setError(
        err?.message ||
          "An error occurred while configuring the terminal. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = !isSubmitting;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Terminal Configuration"
      size="lg"
      closeOnClickOutside={!isSubmitting}
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size="1rem" />}
            color="red"
            variant="light"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Tipping Section */}
        <Box className={styles.sectionCard}>
          <Group gap="xs" mb="sm">
            <IconPercentage
              size="1.25rem"
              color="var(--mantine-color-teal-6)"
            />
            <Text fw={600} style={{ position: "relative", top: "1px" }}>
              Tip Configuration
            </Text>
          </Group>

          <Stack gap="md">
            {/* Smart Tip Threshold */}
            <Group justify="space-between" align="center">
              <Checkbox
                checked={useSmartTip}
                onChange={(e) => setUseSmartTip(e.currentTarget.checked)}
                color="teal"
                size="sm"
                label={
                  <Group gap={4} align="center">
                    <Text
                      style={{
                        fontSize: "1rem", // 16px for accessibility
                        position: "relative",
                        top: "1px", // Vertically align with other inputs
                      }}
                    >
                      Smart Tip Threshold
                    </Text>
                    <Tooltip
                      label="Orders under this amount will show fixed dollar tips, and orders over will show percentage-based tips."
                      multiline
                      w={250}
                      withArrow
                    >
                      <IconInfoCircle
                        size="0.875rem"
                        color="var(--mantine-color-dimmed)"
                        style={{ cursor: "help" }}
                      />
                    </Tooltip>
                  </Group>
                }
              />
              {useSmartTip && (
                <NumberInput
                  value={
                    tipping.smart_tip_threshold !== null
                      ? tipping.smart_tip_threshold / 100
                      : ""
                  }
                  onChange={(val) => {
                    const numVal =
                      typeof val === "string" ? parseFloat(val) : val;
                    setTipping({
                      ...tipping,
                      smart_tip_threshold: isNaN(numVal)
                        ? null
                        : Math.round(numVal * 100),
                    });
                  }}
                  min={0}
                  prefix="$"
                  decimalScale={2}
                  size="sm"
                  placeholder="e.g., $10.00"
                  w={120}
                />
              )}
            </Group>

            {/* Tip type selector (only when smart tip is off) */}
            {!useSmartTip && (
              <SegmentedControl
                value={tipType}
                onChange={(val) =>
                  setTipType(val as "percentages" | "fixed_amounts")
                }
                data={[
                  { label: "Percentages", value: "percentages" },
                  { label: "Fixed Amounts", value: "fixed_amounts" },
                ]}
                color="teal"
                size="sm"
              />
            )}

            {/* Tip Percentages */}
            {(useSmartTip || tipType === "percentages") && (
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Tip Percentages
                </Text>
                <Group grow gap="xs">
                  {tipping.percentages.map((pct, index) => (
                    <NumberInput
                      key={index}
                      value={pct}
                      onChange={(val) => updatePercentage(index, val)}
                      min={1}
                      max={100}
                      suffix="%"
                      size="sm"
                    />
                  ))}
                </Group>
              </Stack>
            )}

            {/* Fixed Amounts */}
            {(useSmartTip || tipType === "fixed_amounts") && (
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Fixed Tip Amounts
                </Text>
                <Group grow gap="xs">
                  {tipping.fixed_amounts.map((amtCents, index) => (
                    <NumberInput
                      key={index}
                      value={amtCents / 100}
                      onChange={(val) => updateFixedAmount(index, val)}
                      min={0.01}
                      prefix="$"
                      decimalScale={2}
                      size="sm"
                    />
                  ))}
                </Group>
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Splashscreen Section */}
        <Box className={styles.sectionCard}>
          <Group gap="xs" mb="sm">
            <IconPhoto size="1.25rem" color="var(--mantine-color-teal-6)" />
            <Text fw={600} style={{ position: "relative", top: "1px" }}>
              Splash Screen (Optional)
            </Text>
          </Group>

          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              Upload a 720x1280px image (png, jpg) to display on the terminal
              screen.
            </Text>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleFileInputChange}
              style={{ display: "none" }}
            />

            {splashscreenPreview ? (
              <Stack gap="xs" align="center">
                <div className={styles.previewContainer}>
                  <img
                    src={splashscreenPreview}
                    alt="Splash screen preview"
                    className={styles.previewImage}
                  />
                  <CloseButton
                    className={styles.removeImageButton}
                    size="sm"
                    variant="filled"
                    color="dark"
                    onClick={removeSplashscreen}
                  />
                </div>
                <Text size="xs" c="dimmed">
                  {splashscreenFilename}
                </Text>
              </Stack>
            ) : (
              <div
                className={styles.dropzone}
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <Stack align="center" gap="xs">
                  <IconUpload size="2rem" color="var(--mantine-color-gray-5)" />
                  <Text size="sm" c="dimmed">
                    Click or drag an image here
                  </Text>
                  <Text size="xs" c="dimmed">
                    Max {MAX_IMAGE_SIZE_MB}MB
                  </Text>
                </Stack>
              </div>
            )}
          </Stack>
        </Box>

        {/* Submit */}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="teal"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
          >
            {isSubmitting ? "Configuring..." : "Save Configuration"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default TerminalConfigurationModal;
